import { Body, Chip, SquareButton, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';

import { Avatar, ChildCell, ColumnHead, LinkButton, Status } from '@/components/bits';
import { Sheet } from '@/components/Sheet';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import {
  useErrorMessage,
  useApprovals,
  useApprove,
  useDeny,
} from '@/data/hooks';
import type { ShellContext } from '@/shell/Shell';
import { relativeTime } from '@/lib/time';
import type { DenyReason, OrgApprovalItem, OrgGroup } from '@/types/wire';

type Proof = { id: string };

/**
 * The photo's three states, told apart by the API rather than guessed from a failed image (Evan, 2026-09-16):
 * `photoUrl` → draw it; `photoExpiresAt` in the past and no `photoUrl` → it existed and has expired, so say so;
 * neither → the note. A URL is valid for ten minutes, so an image that fails to load asks for the list again once —
 * a fresh URL, not a broken picture.
 */
function ProofSheet({
  item,
  onClose,
  onStaleUrl,
}: {
  item: OrgApprovalItem;
  onClose: () => void;
  onStaleUrl: () => void;
}) {
  const photo = item.proof === 'photo';
  const expired = photo && !item.photoUrl && !!item.photoExpiresAt;
  const [retried, setRetried] = useState(false);
  return (
    <Sheet
      title={
        <Text variant="cardTitle" tone="ink">
          {photo ? placeholders.proof.photoTitle : placeholders.proof.noteTitle}
        </Text>
      }
      onClose={onClose}
      testID="proof-sheet"
    >
      <div className="result-head">
        <Avatar child={item.child} size={40} />
        <div className="cell">
          <Text variant="rowTitleSm" tone="ink">
            {deck.phone.cardTitle(item.child.firstName, item.task.title)}
          </Text>
          <Text variant="meta" tone="muted">
            {relativeTime(item.at)}
          </Text>
        </div>
      </div>
      {photo && item.photoUrl ? (
        // View only: no download attribute, no link to the URL, no context-menu save (pointer-events off).
        <img
          className="proof-photo"
          src={item.photoUrl}
          alt={placeholders.proof.photoAlt}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onError={() => {
            if (retried) return;
            setRetried(true);
            onStaleUrl();
          }}
        />
      ) : expired ? (
        <>
          <Body tone="muted" testID="photo-expired">
            {placeholders.proof.photoExpired}
          </Body>
          {item.note ? <Body tone="ink">{item.note}</Body> : null}
        </>
      ) : (
        <Body tone="ink">{item.note ?? ''}</Body>
      )}
    </Sheet>
  );
}

function DenySheet({
  item,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  item: OrgApprovalItem;
  busy: boolean;
  /** The DataClient code of a failed deny; the sheet stays open so the staff member can retry or cancel. */
  error: string | null;
  onConfirm: (reason: DenyReason, text: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<DenyReason | null>(null);
  const [text, setText] = useState('');
  return (
    <Sheet
      title={
        <Text variant="cardTitle" tone="ink">
          {placeholders.deny.title}
        </Text>
      }
      onClose={onClose}
      testID="deny-sheet"
    >
      <Text variant="bodySm" tone="muted">
        {deck.phone.cardTitle(item.child.firstName, item.task.title)}
      </Text>
      <div className="chips">
        {deck.denyReasons.map((r) => (
          <Chip
            key={r.reason}
            label={r.label}
            selected={reason === r.reason}
            onPress={() => setReason(r.reason)}
            testID={`deny-${r.reason}`}
          />
        ))}
      </div>
      <TextField
        placeholder={placeholders.deny.notePlaceholder}
        value={text}
        onChangeText={setText}
        testID="deny-text"
      />
      <div className="button-pair">
        <WasabiButton
          label={placeholders.deny.cancel}
          variant="outline"
          faceHeight={48}
          onPress={onClose}
        />
        <WasabiButton
          label={placeholders.deny.confirm}
          disabled={reason === null}
          loading={busy}
          faceHeight={48}
          onPress={() => reason && onConfirm(reason, text.trim())}
          testID="deny-confirm"
        />
      </div>
      {error ? (
        <Text variant="helper" tone="error" testID="deny-error">
          {error}
        </Text>
      ) : null}
    </Sheet>
  );
}

function proofLabel(item: OrgApprovalItem): string | null {
  return item.proof === 'note'
    ? deck.approvals.note
    : item.proof === 'photo'
      ? deck.approvals.photo
      : null;
}

/** "Homework Club · Reading Week": the groups of this person's that a row's child is in, by name. */
function groupLabel(item: OrgApprovalItem, groups: ReadonlyArray<OrgGroup>): string | undefined {
  const names = groups.filter((g) => item.child.groupIds.includes(g.id)).map((g) => g.name);
  return names.length ? deck.roster.joinNames(names) : undefined;
}

/**
 * **O01 · ONE LIST ACROSS EVERY GROUP THE PERSON STAFFS** (the 23 Oct audit, T1-2, T1-3 and Tier 4 #8, Evan's
 * recommendation taken). The list used to show the first group only, with no way to pick another on a phone, and it
 * hid anything older than today behind "N more from yesterday", so the pill could say 2 while the list said "Nothing
 * waiting". Now:
 *
 * - every row the API sends is on the list (the API already scopes it to the groups this person staffs), oldest first,
 *   each labelled with its group while there is more than one group to tell apart;
 * - with two or more groups, a chip row narrows it ("All 3", "Homework Club · 2"), on a phone and on a desktop;
 * - "Nothing waiting" only when the list is truly empty, so the nav pill, the title, the chip and Approve all count
 *   the same rows.
 */
export function ApprovalsScreen() {
  const errorMessage = useErrorMessage();
  const { org, isPhone } = useOutletContext<ShellContext>();
  const navigate = useNavigate();
  const approvals = useApprovals();
  const approve = useApprove();
  const deny = useDeny();
  /** null = every group ("All"); otherwise one group's rows. */
  const [groupId, setGroupId] = useState<string | null>(null);
  const [proof, setProof] = useState<Proof | null>(null);
  const [denying, setDenying] = useState<OrgApprovalItem | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  /** What to say about the last failed approve / deny / approve-all — a sentence, never a code. */
  const [actionError, setActionError] = useState<string | null>(null);

  const groups = useMemo(() => org?.groups ?? [], [org]);
  const several = groups.length >= 2;
  // a group that is no longer this person's (or a stale pick) falls back to every group, never to an empty list
  const group = groups.find((g) => g.id === groupId);

  const all = useMemo(() => approvals.data ?? [], [approvals.data]);
  const visible = useMemo(
    () => (group ? all.filter((a) => a.child.groupIds.includes(group.id)) : all),
    [all, group],
  );
  const waiting = visible.length;
  const label = (item: OrgApprovalItem) => (several ? groupLabel(item, groups) : undefined);
  /** The group and the assignment, once each: a club often names an assignment after its group. */
  const metaNames = (item: OrgApprovalItem) => {
    const group = label(item);
    return [group, item.task.assignmentTitle === group ? undefined : item.task.assignmentTitle];
  };

  // Every mutation surfaces its DataClient code (brief rule 10): a 409 from a colleague approving first, a 403,
  // a network drop. UNAUTHORIZED additionally signs the user out from SessionProvider.
  const onApprove = async (item: OrgApprovalItem) => {
    setBusyId(item.id);
    setActionError(null);
    try {
      await approve.mutateAsync(item.id);
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };
  const onDeny = async (reason: DenyReason, text: string) => {
    if (!denying) return;
    setBusyId(denying.id);
    setActionError(null);
    try {
      await deny.mutateAsync({
        completionId: denying.id,
        input: text ? { reason, text } : { reason },
      });
      setDenying(null);
    } catch (e) {
      setActionError(errorMessage(e));
    } finally {
      setBusyId(null);
    }
  };
  // **"Approve all N" approves exactly the N rows on screen** (Evan, 13 Oct 2026: "whatever you feel is best"): every
  // row under "All", or the chosen group's. It used to POST …/approvals/approve-all, which approved every group the
  // staff member can see, so one tap could approve children nobody had looked at; that route now needs a group, and
  // the portal still does not call it. Each row's approve is checked by the server on its own (the completion must be
  // for a child this staff member covers), so "All" can only ever approve rows this person was shown.
  // One approve per row, in order; each failure is turned into a sentence and deduped, because ten rows refused
  // for one reason is one thing to say, not ten.
  const [approvingAll, setApprovingAll] = useState(false);
  const onApproveAll = async () => {
    setActionError(null);
    setApprovingAll(true);
    const problems: string[] = [];
    for (const item of visible) {
      try {
        await approve.mutateAsync(item.id);
      } catch (e) {
        problems.push(errorMessage(e));
      }
    }
    setApprovingAll(false);
    if (problems.length > 0) setActionError([...new Set(problems)].join(' '));
  };

  const actions = (item: OrgApprovalItem) => (
    <div className="cell-actions">
      <SquareButton
        kind="deny"
        onPress={() => setDenying(item)}
        disabled={busyId === item.id}
        accessibilityLabel={`${placeholders.deny.denyA11y} ${item.child.firstName}`}
        testID={`deny-${item.id}`}
      />
      <SquareButton
        kind="approve"
        onPress={() => void onApprove(item)}
        disabled={busyId === item.id}
        accessibilityLabel={`${placeholders.deny.approveA11y} ${item.child.firstName}`}
        testID={`approve-${item.id}`}
      />
    </div>
  );

  const status = approvals.isPending
    ? placeholders.empty.loading
    : approvals.error
      ? errorMessage(approvals.error)
      : visible.length === 0
        ? placeholders.empty.approvals
        : null;

  // the roster's chips ("All 38", "Homework Club · 14"), drawn only when there is more than one group
  const chips = several ? (
    <div className="chips" data-testid="group-chips">
      <Chip
        label={deck.roster.chipAll(all.length)}
        selected={!group}
        onPress={() => setGroupId(null)}
        testID="group-chip-all"
      />
      {groups.map((g) => (
        <Chip
          key={g.id}
          label={deck.roster.chipCount(
            g.name,
            all.filter((a) => a.child.groupIds.includes(g.id)).length,
          )}
          selected={group?.id === g.id}
          onPress={() => setGroupId(g.id)}
          testID={`group-chip-${g.id}`}
        />
      ))}
    </div>
  ) : null;

  const sheets = (
    <>
      {proof && all.find((a) => a.id === proof.id) ? (
        <ProofSheet
          // read from the live list, so a refetch hands the open sheet its fresh URL
          item={all.find((a) => a.id === proof.id)!}
          onClose={() => setProof(null)}
          onStaleUrl={() => void approvals.refetch()}
        />
      ) : null}
      {denying ? (
        <DenySheet
          item={denying}
          busy={busyId === denying.id}
          error={actionError}
          onConfirm={(r, t) => void onDeny(r, t)}
          onClose={() => {
            setDenying(null);
            setActionError(null);
          }}
        />
      ) : null}
    </>
  );

  if (isPhone)
    return (
      <>
        {chips}
        {status ? <Status>{status}</Status> : null}
        {actionError && !denying ? <Status testID="action-error">{actionError}</Status> : null}
        {visible.map((item) => (
          <div className="card-row" key={item.id} data-testid={`row-${item.id}`}>
            <Avatar child={item.child} size={40} />
            <div className="cell grow">
              <Text variant="rowTitle" tone="ink" numberOfLines={2}>
                {deck.phone.cardTitle(item.child.firstName, item.task.title)}
              </Text>
              {/* two lines: the group's name is what tells two groups apart, and one line cut it off */}
              <Text variant="meta" tone="muted" numberOfLines={2}>
                {deck.phone.cardMeta([
                  relativeTime(item.at),
                  ...metaNames(item),
                  item.proof === 'note'
                    ? deck.phone.proofNote
                    : item.proof === 'photo'
                      ? deck.phone.proofPhoto
                      : undefined,
                ])}
              </Text>
              {proofLabel(item) ? (
                <LinkButton
                  label={proofLabel(item) ?? ''}
                  onPress={() => setProof({ id: item.id })}
                />
              ) : null}
            </div>
            {actions(item)}
          </div>
        ))}
        <div className="sticky-footer">
          <WasabiButton
            label={deck.approvals.approveAll(visible.length)}
            onPress={() => void onApproveAll()}
            disabled={visible.length === 0 || approvingAll}
            loading={approvingAll}
          />
          <WasabiButton
            label={deck.phone.code}
            variant="outline"
            faceHeight={52}
            fullWidth={false}
            onPress={() => navigate('/redemptions')}
          />
        </div>
        {sheets}
      </>
    );

  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{deck.approvals.title(waiting)}</Title2>
          <Text variant="bodySm" tone="muted">
            {deck.approvals.subtitle}
          </Text>
        </div>
        <div className="actions">
          <WasabiButton
            label={deck.approvals.approveAll(visible.length)}
            size="sm"
            fullWidth={false}
            onPress={() => void onApproveAll()}
            disabled={visible.length === 0 || approvingAll}
            loading={approvingAll}
            testID="approve-all"
          />
        </div>
      </div>
      {chips}
      <div className="table-card">
        <div className="table-scroll">
          <div className="grid-head grid-approvals">
            <ColumnHead>{deck.approvals.columns.child}</ColumnHead>
            <ColumnHead>{deck.approvals.columns.task}</ColumnHead>
            <ColumnHead>{deck.approvals.columns.submitted}</ColumnHead>
            <ColumnHead>{deck.approvals.columns.proof}</ColumnHead>
            <span />
          </div>
          {visible.map((item) => (
            <div className="grid-row grid-approvals" key={item.id} data-testid={`row-${item.id}`}>
              <ChildCell child={item.child} />
              <div className="cell">
                <Text variant="rowTitleSm" tone="ink" numberOfLines={1}>
                  {item.task.title}
                </Text>
                {item.task.assignmentTitle || label(item) ? (
                  <Text variant="meta" tone="muted" numberOfLines={1} testID={`row-meta-${item.id}`}>
                    {deck.phone.cardMeta(metaNames(item))}
                  </Text>
                ) : null}
              </div>
              <Text variant="bodySm" tone="slate">
                {relativeTime(item.at)}
              </Text>
              {proofLabel(item) ? (
                <div>
                  <LinkButton
                    label={proofLabel(item) ?? ''}
                    onPress={() => setProof({ id: item.id })}
                    testID={`proof-${item.id}`}
                  />
                </div>
              ) : (
                <Text variant="bodySm" tone="muted">
                  {deck.approvals.dash}
                </Text>
              )}
              {actions(item)}
            </div>
          ))}
        </div>
        {status ? (
          <div className="grid-row grid-approvals">
            <Status>{status}</Status>
          </div>
        ) : null}
        {actionError && !denying ? (
          <div className="grid-row grid-approvals">
            <Status testID="action-error">{actionError}</Status>
          </div>
        ) : null}
      </div>
      {sheets}
    </>
  );
}
